import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {
  TOS_PATH,
  PAYMENT_PROTECTION_POLICY_PATH,
  CHARGEBACKS_AND_FEES_PATH,
  ROOT_HOST
} from 'dpl/shared/constants/urls';
import MiniOverlayModal from 'dpl/common/mini_overlay_modal/components/MiniOverlayModal';
import { isMobileUA } from 'dpl/shared/utils';
import StripeChargeModalFixedTooltipWrapper from 'dpl/components/StripeCharge/New/StripeChargeModal/StripeChargeModalFixedTooltipWrapper';
import { sendAnalyticsEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';

import { ensureCurrencyIsPresent } from './util';

const IS_MOBILE_UA = isMobileUA();

function protectionSupportConfirmationCheck(dogPayment, amount) {
  const amountText = amount
    ? ensureCurrencyIsPresent(parseFloat(amount).toFixed(2))
    : '';

  const goodDogPaymentProtectionLink = (
    <a
      className="underline"
      href={PAYMENT_PROTECTION_POLICY_PATH}
      target="_blank"
      rel="noreferrer"
    >
      Good Dog Payment Protection
    </a>
  );
  const termsOfServiceLink = (
    <a
      className="underline"
      href={`${ROOT_HOST}${TOS_PATH}`}
      target="_blank"
      rel="noreferrer"
    >
      Terms of Service
    </a>
  );
  const buyerRefundAndDisputeLink = (
    <a
      className="underline"
      href={`${ROOT_HOST}${CHARGEBACKS_AND_FEES_PATH}`}
      target="_blank"
      rel="noreferrer"
    >
      Buyer Refund &amp; Card Dispute Policy
    </a>
  );

  return dogPayment ? (
    <p>
      If you switch your payment type to Friends & Family, your {amountText}{' '}
      payment will not be covered by {goodDogPaymentProtectionLink}, our{' '}
      {termsOfServiceLink}, and the {buyerRefundAndDisputeLink}. You will also
      lose access to Good Dog’s Puppy Training Program, valued at $235.
    </p>
  ) : (
    <p>
      If you switch your payment type to Friends & Family, your {amountText}{' '}
      payment will not be covered by {goodDogPaymentProtectionLink}, our{' '}
      {termsOfServiceLink}, and the {buyerRefundAndDisputeLink}.
    </p>
  );
}

function SelectPaymentTypeConfirmationContent({
  amount,
  dogPayment,
  onCancelConfirm,
  onKeepConfirm
}) {
  return (
    <div
      className={classnames(
        'SelectPaymentTypeConfirmation pt4 ph4 pv12-sm ph20-sm tc f2',
        {
          'bt b--lightest-gray mt12': IS_MOBILE_UA
        }
      )}
    >
      <div className="mb2 mb4-sm">
        <h4 className="f4 f6-sm lh-normal mb2">
          Whoa! Are you sure you want to lose payment protection?
        </h4>
        <p className="f2 mb8 secondary">
          {protectionSupportConfirmationCheck(dogPayment, amount)}
        </p>
      </div>
      <button
        type="button"
        className="bg-blue-500 white fw-medium w-100 pv4 br2 mb2 font-16"
        onClick={onKeepConfirm}
      >
        Never mind, I&apos;d like to keep it
      </button>
      <button
        type="button"
        className="button rubber bg-near-white b--transparent fw-medium font-16 w-100 pv4 br2"
        onClick={onCancelConfirm}
      >
        I don&apos;t want to protect my payment
      </button>
    </div>
  );
}

export default function SelectPaymentTypeConfirmation({
  onKeepConfirm,
  onCancelConfirm,
  isVisible,
  ...rest
}) {
  useEffect(() => {
    if (isVisible) {
      sendAnalyticsEvent(ANALYTICS_EVENTS.PAYMENT_MODAL_VIEWED_BY_BUYER, {
        view: ANALYTICS_VIEWS.PROTECTION_SUPPORT_CHANGE_CONFIRMATION,
        view_feature: ANALYTICS_VIEW_FEATURES.PAYMENT_CHECKOUT_PAGE
      });
    }
  }, [isVisible]);

  if (IS_MOBILE_UA) {
    return (
      <StripeChargeModalFixedTooltipWrapper
        isVisible={isVisible}
        onCloseTooltipClick={onKeepConfirm}
        title={
          <SelectPaymentTypeConfirmationContent
            {...rest}
            onCancelConfirm={onCancelConfirm}
            onKeepConfirm={onKeepConfirm}
          />
        }
      />
    );
  }

  return (
    <>
      {isVisible && (
        <MiniOverlayModal onCloseClick={onKeepConfirm}>
          <SelectPaymentTypeConfirmationContent
            {...rest}
            onCancelConfirm={onCancelConfirm}
            onKeepConfirm={onKeepConfirm}
          />
        </MiniOverlayModal>
      )}
    </>
  );
}

SelectPaymentTypeConfirmationContent.propTypes = {
  amount: PropTypes.string.isRequired,
  dogPayment: PropTypes.bool,
  onCancelConfirm: PropTypes.func.isRequired,
  onKeepConfirm: PropTypes.func.isRequired
};

SelectPaymentTypeConfirmationContent.defaultProps = {
  dogPayment: null
};

SelectPaymentTypeConfirmation.propTypes = {
  amount: PropTypes.string.isRequired,
  dogPayment: PropTypes.bool,
  onCancelConfirm: PropTypes.func.isRequired,
  onKeepConfirm: PropTypes.func.isRequired,
  isVisible: PropTypes.bool
};

SelectPaymentTypeConfirmation.defaultProps = {
  dogPayment: false,
  isVisible: false
};
